import React, { memo, useState, useEffect, useMemo } from 'react';
import { Box } from "@mui/system";
import { 
    IconButton, 
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Loader } from "../../Loader";
import Plot from "../../Plots";
import { useDownloadHandler } from '../hooks/useDownloadHandler';

// Import components directly from their files instead of from index.js to avoid circular dependencies
import QuestionTitle from './QuestionTitle';
import QuestionScore from './QuestionScore';

const QuestionContent = memo(({ 
    element,
    plotLanguage,
    klausimas,
    visiAts,
    atsakymai,
    tipai,
    metai,
    handleCapture,
    questionRef,
    id,
    isActive,
    setActiveQuestionId,
    isExpanded: controlledExpanded,
    onExpand
}) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [updateKey, setUpdateKey] = useState(0);

    // Set initial expansion state only once after mount
    useEffect(() => {
        const shouldExpand = element.name.includes(klausimas) || isActive;
        if (shouldExpand) {
            onExpand?.(element.name, true);
            if (setActiveQuestionId && element.name) {
                setActiveQuestionId(element.name);
            }
        }
    // Run this effect only once on mount, and when klausimas changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const questionData = useMemo(() => {
        if (!tipai || !metai || !element.pageName || !element.name) {
            return null;
        }

        const formData = Array.isArray(tipai) ? 
            tipai.find(form => {
                const validTo = form.attributes?.validTo;
                return validTo ? new Date(validTo).getFullYear() === metai : false;
            }) : null;

        if (!formData?.attributes?.formObject?.pages) {
            return null;
        }

        const page = formData.attributes.formObject.pages
            .find(({ name }) => name === element.pageName);
            
        return page?.elements.find(({ name }) => name === element.name) || null;
    }, [tipai, metai, element.pageName, element.name]);

    // Modified effect to respond to both expansion state and filtered answer changes
    useEffect(() => {
        let timeoutId;
        if (controlledExpanded && questionData) {
            timeoutId = setTimeout(() => {
                setIsLoaded(true);
                // Update the key to force Plot component to re-render
                setUpdateKey(prev => prev + 1);
            }, 300);
        } else {
            setIsLoaded(false);
        }
        return () => timeoutId && clearTimeout(timeoutId);
    }, [controlledExpanded, questionData, atsakymai]); // Added atsakymai dependency to refresh on filter changes

    // Improved handleChange to better manage expand/collapse state
    const handleChange = (event, expanded) => {
        // Explicitly handle collapse case to ensure we properly collapse
        if (controlledExpanded && !expanded) {
            setIsLoaded(false);
        }

        // Call parent handler with the desired expand state
        onExpand?.(element.name, expanded);

        if (expanded && setActiveQuestionId && element.name) {
            setActiveQuestionId(element.name);
        }
    };

    const handleDownload = useDownloadHandler(element, plotLanguage, handleCapture);
    
    const accordionId = id || `question-${element.name}`;
    const isQuestionActive = isActive || element.name.includes(klausimas);
    
    return (
        <Accordion 
            className={`question-accordion ${isQuestionActive ? 'active' : ''}`}
            expanded={controlledExpanded}
            onChange={handleChange}
            id={accordionId}
            TransitionProps={{ unmountOnExit: true }}
            disableGutters
        >
            <AccordionSummary
                expandIcon={
                    <ExpandMoreIcon 
                        sx={{ 
                            transform: controlledExpanded ? 'rotate(180deg)' : 'rotate(0)',
                            transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)'
                        }} 
                    />
                }
                className="question-header"
                id={`${accordionId}-header`}
                aria-controls={`${accordionId}-content`}
            >
                <div className="question-summary">
                    <QuestionTitle 
                        title={element.title}
                        plotLanguage={plotLanguage}
                        name={element.name}
                    />
                    <div className="question-actions">
                        <QuestionScore 
                            name={element.name}
                            score={visiAts[element.name]}
                        />
                    </div>
                </div>
            </AccordionSummary>
            <AccordionDetails 
                sx={{ overflow: 'visible' }}
                id={`${accordionId}-content`}
                role="region"
                aria-labelledby={`${accordionId}-header`}
            >
                <div className="question-content-wrapper">
                    <IconButton
                        sx={{ 
                            display: { xs: "none", sm: "none", md: "inline-flex" },
                            position: 'absolute',
                            right: '24px',
                            top: '24px',
                            zIndex: 1
                        }}
                        data-html2canvas-ignore="true"
                        color="primary"
                        onClick={handleDownload}
                        aria-label="Download question content"
                    >
                        <FileDownloadIcon />
                    </IconButton>
                    <div 
                        ref={isQuestionActive ? questionRef : null} 
                        className="question-content"
            style={{ 
                minHeight: '200px',
                opacity: controlledExpanded ? 1 : 0,
                transition: 'opacity 300ms ease-in-out'
            }}
                    >
                        {!isLoaded ? (
                            <Box 
                                sx={{ 
                                    p: 2, 
                                    display: 'flex', 
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '200px'
                                }}
                            >
                                <Loader />
                            </Box>
                        ) : questionData ? (
                            <Box 
                                sx={{ 
                                    opacity: isLoaded ? 1 : 0, 
                                    transition: 'opacity 0.3s ease-in-out'
                                }}
                                role="region"
                                aria-label="Question plot"
                            >
                                <Plot
                                    key={updateKey}
                                    atsakymai={atsakymai}
                                    klausimas={questionData}
                                />
                            </Box>
                        ) : (
                            <Box 
                                sx={{ 
                                    p: 2, 
                                    textAlign: 'center', 
                                    color: 'text.secondary',
                                    height: '200px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                Klausimo duomenys nerasti
                            </Box>
                        )}
                    </div>
                </div>
            </AccordionDetails>
        </Accordion>
    );
}, (prevProps, nextProps) => {
    // Always re-render when filtered answers change to ensure diagrams update
    if (prevProps.atsakymai !== nextProps.atsakymai) {
        return false;
    }

    // Basic comparisons for important props
    if (prevProps.isActive !== nextProps.isActive) return false;
    if (prevProps.element.name !== nextProps.element.name) return false;
    if (prevProps.plotLanguage !== nextProps.plotLanguage) return false;
    if (prevProps.klausimas !== nextProps.klausimas) return false;
    if (prevProps.metai !== nextProps.metai) return false;
    if (prevProps.isExpanded !== nextProps.isExpanded) return false;

    // Always check for answer scores change
    const prevAnswers = prevProps.visiAts[prevProps.element.name];
    const nextAnswers = nextProps.visiAts[nextProps.element.name];
    if (prevAnswers !== nextAnswers) {
        return false;
    }

    // Check if form types changed
    if (prevProps.tipai !== nextProps.tipai) {
        // If form reference changed, check if the relevant year form changed
        const prevYear = prevProps.tipai?.find(t => {
            const validTo = t.attributes?.validTo;
            return validTo ? new Date(validTo).getFullYear() === prevProps.metai : false;
        });
        const nextYear = nextProps.tipai?.find(t => {
            const validTo = t.attributes?.validTo;
            return validTo ? new Date(validTo).getFullYear() === nextProps.metai : false;
        });

        if (prevYear?.id !== nextYear?.id) return false;
    }

    // Generate new updateKey to force Plot refresh if expanded
    if (prevProps.isExpanded && nextProps.isExpanded &&
        prevProps.atsakymai !== nextProps.atsakymai) {
        // Re-render if answers changed while expanded
        return false;
    }

    return true;
});

QuestionContent.displayName = 'QuestionContent';

export default QuestionContent;
